import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import PageMeta from '../SEO/PageMeta';
import { Search, Share2, Check, X, ArrowLeft, LayoutGrid } from 'lucide-react';
import logo from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg'; // change the file name if yours differs
import CommentSection from '../CommentSection/CommentSection';
import RichContent, { isHtml } from '../RichText/RichContent';
import './PublicPYQPage.css';

const BACKEND = process.env.REACT_APP_API_URL || 'https://civilfox-backend.onrender.com/api';

// In-memory caches: revisiting a question or an exam list is instant
const qData = new Map();
const qInflight = new Map();
const stackCache = new Map();

function getQuestion(id) {
  if (qData.has(id)) return Promise.resolve(qData.get(id));
  if (!qInflight.has(id)) {
    qInflight.set(id, axios.get(`${BACKEND}/public/pyq-question/${id}`)
      .then(r => { qData.set(id, r.data.question); return r.data.question; })
      .finally(() => qInflight.delete(id)));
  }
  return qInflight.get(id);
}

function useDebounce(val, ms) {
  const [dv, setDv] = useState(val);
  useEffect(() => {
    const t = setTimeout(() => setDv(val), ms);
    return () => clearTimeout(t);
  }, [val, ms]);
  return dv;
}

/* ── Text helpers ─────────────────────────────────────── */
function splitIntroClosing(text) {
  if (!text) return { intro: '', closing: '' };
  const lines = text.split('\n').filter(l => l.trim());
  if (lines.length <= 1) return { intro: text, closing: '' };
  return { intro: lines[0], closing: lines.slice(1).join('\n') };
}

/* ── Table parsing: pipe tables, tab tables and "List I / List II" text become real tables ── */
const SEP = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const LIST1 = /^\W*list\s*[-–—]?\s*(?:i|1)\b/i;
const LIST2 = /list\s*[-–—]?\s*(?:ii|2)\b/i;
const LEFT = /^\(?([A-Ea-e])[.)]\)?\s+(.+)$/;
const RIGHT = /^\(?([1-9])[.)]\)?\s+(.+)$/;
const cells = line => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

/* Finds a table starting at lines[i]: pipe tables, tab tables and "List I / List II" text */
function tryTable(lines, i) {
  const line = lines[i].trim();
  const next = (lines[i + 1] || '').trim();

  if (line.includes('|') && cells(line).length >= 2) {
    if (SEP.test(next)) {
      const rows = [];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) { rows.push(cells(lines[j])); j += 1; }
      return { head: cells(line), rows, next: j };
    }
    if (next.includes('|') && cells(next).length >= 2) {
      const all = [];
      let j = i;
      while (j < lines.length && lines[j].includes('|') && !SEP.test(lines[j].trim())) { all.push(cells(lines[j])); j += 1; }
      if (all.length >= 2) return { head: all[0], rows: all.slice(1), next: j };
    }
  }

  if (line.includes('\t') && next.includes('\t')) {
    const all = [];
    let j = i;
    while (j < lines.length && lines[j].includes('\t')) { all.push(lines[j].trim().split(/\t+/)); j += 1; }
    return { head: all[0], rows: all.slice(1), next: j };
  }

  if (LIST1.test(line)) {
    if (LIST2.test(line)) {
      const head = [line.slice(0, line.search(LIST2)).trim(), line.slice(line.search(LIST2)).trim()];
      const rows = [];
      const lefts = [];
      const rights = [];
      let j = i + 1;
      while (j < lines.length) {
        const t = lines[j].trim();
        if (!t) break;
        const parts = t.split(/\t+|\s{2,}/);
        const both = t.match(/^\(?([A-Ea-e])[.)]\)?\s+(.+?)\s+\(?([1-9])[.)]\)?\s+(.+)$/);
        if (parts.length >= 2 && LEFT.test(parts[0]) && RIGHT.test(parts[1])) rows.push([parts[0], parts[1]]);
        else if (both) rows.push([`${both[1]}. ${both[2]}`, `${both[3]}. ${both[4]}`]);
        else if (LEFT.test(t) && !rights.length) lefts.push(t);
        else if (RIGHT.test(t)) rights.push(t);
        else break;
        j += 1;
      }
      for (let r = 0; r < Math.max(lefts.length, rights.length); r += 1) rows.push([lefts[r] || '', rights[r] || '']);
      if (rows.length) return { head, rows, next: j };
    } else {
      const lefts = [];
      let j = i + 1;
      while (j < lines.length && LEFT.test(lines[j].trim())) { lefts.push(lines[j].trim()); j += 1; }
      if (lefts.length && j < lines.length && LIST2.test(lines[j].trim())) {
        const head = [line, lines[j].trim()];
        const rights = [];
        j += 1;
        while (j < lines.length && RIGHT.test(lines[j].trim())) { rights.push(lines[j].trim()); j += 1; }
        if (rights.length) return { head, rows: lefts.map((l, r) => [l, rights[r] || '']), next: j };
      }
    }
  }
  return null;
}

function TableBlock({ t }) {
  return (
    <div className="pp-match">
      <table>
        <thead><tr>{t.head.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
        <tbody>{t.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function FormattedText({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="pp-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { out.push(<div key={i} className="pp-gap" />); i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const bullet = /^[•\-*→]\s/.test(t);
    const clean = t.replace(/^\d+[.)]\s*|^[•\-*→]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="pp-li"><b>{num[1]}.</b><span>{clean}</span></div>);
    else if (bullet) out.push(<div key={i} className="pp-li"><b>•</b><span>{clean}</span></div>);
    else out.push(<p key={i}>{t}</p>);
    i += 1;
  }
  return <div className="pp-text">{out}</div>;
}

function FormattedQuestion({ text }) {
  if (!text) return null;
  if (isHtml(text)) return <div className="pp-qtext pp-rich"><RichContent html={text} /></div>;
  const lines = text.split('\n');
  const out = [];
  for (let i = 0; i < lines.length;) {
    const t = lines[i].trim();
    if (!t) { i += 1; continue; }
    const tb = tryTable(lines, i);
    if (tb) { out.push(<TableBlock key={i} t={tb} />); i = tb.next; continue; }
    const num = t.match(/^(\d+)[.)]\s/);
    const clean = t.replace(/^\d+[.)]\s*|\*\*/g, '').trim();
    if (num) out.push(<div key={i} className="pp-li"><b>{num[1]}.</b><span>{clean}</span></div>);
    else out.push(<p key={i} className={/^[A-Z][^a-z]{4,}/.test(t) ? 'pp-strong' : ''}>{t.replace(/\*\*/g, '')}</p>);
    i += 1;
  }
  return <div className="pp-qtext">{out}</div>;
}

function MatchLists({ matchLists }) {
  if (!matchLists) return null;
  const { list1Label = 'List I', list2Label = 'List II', list1Items = [], list2Items = [] } = matchLists;

  const normalize = (items, numeric) => (Array.isArray(items) ? items : [])
    .map((item, i) => ({
      key: typeof item === 'string' ? (numeric ? String(i + 1) : String.fromCharCode(65 + i)) : String(item?.key || (numeric ? i + 1 : String.fromCharCode(65 + i))),
      text: typeof item === 'string' ? item : (typeof item?.text === 'string' ? item.text : ''),
    }))
    .filter(item => item.text.trim());

  const left = normalize(list1Items, false);
  const right = normalize(list2Items, true);
  if (!left.length && !right.length) return null;

  return (
    <div className="pp-match">
      <table aria-label={`${list1Label} and ${list2Label}`}>
        <thead><tr><th>{list1Label}</th><th>{list2Label}</th></tr></thead>
        <tbody>
          {Array.from({ length: Math.max(left.length, right.length) }, (_, i) => (
            <tr key={i}>
              <td>{left[i] && <><b>{left[i].key}.</b> {left[i].text}</>}</td>
              <td>{right[i] && <><b>{right[i].key}.</b> {right[i].text}</>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PublicPYQPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem('vidyacards_token');

  const [card, setCard] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [stack, setStack] = useState([]);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [picked, setPicked] = useState(null);
  const [copied, setCopied] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const debQuery = useDebounce(query, 300);
  const mainRef = useRef(null);

  const q = (card && String(card._id) === String(id) && card) || qData.get(id) || null;

  /* Load the question for this URL */
  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    setNotFound(false);
    getQuestion(id)
      .then(r => { if (live) setCard(r); })
      .catch(() => { if (live) setNotFound(true); });
    return () => { live = false; };
  }, [id]);

  useEffect(() => {
    if (q?.slug && q.slug !== id) {
      navigate(`/pyq-question/${encodeURIComponent(q.slug)}`, { replace: true });
    }
  }, [id, navigate, q?.slug]);

  /* Other questions from the same exam */
  useEffect(() => {
    const exam = q?.exam;
    if (!exam) return undefined;
    if (stackCache.has(exam)) { setStack(stackCache.get(exam)); return undefined; }
    let live = true;
    axios.get(`${BACKEND}/public/search-questions`, { params: { exam, limit: 40 } })
      .then(res => { const list = res.data.results || []; stackCache.set(exam, list); if (live) setStack(list); })
      .catch(() => { if (live) setStack([]); });
    return () => { live = false; };
  }, [q?.exam]);

  /* Search */
  useEffect(() => {
    if (!debQuery.trim()) {
      setFound([]);
      setSearchedQuery('');
      setSearchLoading(false);
      setSearchError(false);
      return undefined;
    }
    let live = true;
    setSearchLoading(true);
    setSearchError(false);
    axios.get(`${BACKEND}/public/search-questions`, { params: { q: debQuery, limit: 40 } })
      .then(res => {
        if (!live) return;
        const results = res.data.results || [];
        setFound(results);
        setSearchedQuery(debQuery.trim());
        if (results[0]?._id) navigate(`/pyq-question/${results[0]._id}`, { replace: true });
      })
      .catch(error => {
        if (!live) return;
        console.error('Failed to search public PYQs', error);
        setFound([]);
        setSearchedQuery(debQuery.trim());
        setSearchError(true);
      })
      .finally(() => { if (live) setSearchLoading(false); });
    return () => { live = false; };
  }, [debQuery, navigate]);

  /* Panel list: search results while searching, otherwise the exam's questions (current one always included) */
  const searching = !!query.trim();
  const searchPending = searching && (
    query.trim() !== debQuery.trim() || searchedQuery !== debQuery.trim() || searchLoading
  );
  const items = useMemo(() => {
    if (searching) return searchPending ? [] : found;
    if (!q || stack.some(r => r._id === id)) return stack;
    return [{ _id: q._id, subject: q.subject, year: q.year, question: q.question, questionType: q.questionType }, ...stack];
  }, [searching, searchPending, found, stack, q, id]);

  const groups = useMemo(() => {
    const m = new Map();
    items.forEach((r, i) => {
      const s = (r.subject || '').trim() || 'General';
      if (!m.has(s)) m.set(s, []);
      m.get(s).push(i);
    });
    return [...m.entries()];
  }, [items]);

  useEffect(() => { setPicked(null); setCopied(false); mainRef.current?.scrollTo?.(0, 0); }, [id]);

  /* Full-screen page: lock the page behind it */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = [html.style.overflow, body.style.overflow];
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => { html.style.overflow = prev[0]; body.style.overflow = prev[1]; };
  }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const goTo = qid => {
    setNavOpen(false);
    const target = items.find(item => String(item._id) === String(qid));
    if (qid !== id) navigate(`/pyq-question/${encodeURIComponent(target?.slug || qid)}`, { replace: true });
  };

  const share = async () => {
    const url = `https://civilfox.app/pyq-question/${id}`;
    try {
      if (navigator.share && /Android|iPhone|iPad|Mobi/i.test(navigator.userAgent)) {
        await navigator.share({ url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }
    } catch { /* share cancelled */ }
  };

  /* Question by type */
  const isMains = q?.questionType === 'mains';
  const isMatch = q?.questionType === 'match-list';
  const lists = q?.matchLists;
  const hasLists = !!lists && ((lists.list1Items || []).length > 0 || (lists.list2Items || []).length > 0);
  const isAR = q?.questionType === 'assertion-reason';
  const isStatement = q?.questionType === 'statement' && q?.statements?.length > 0;
  const side = isMains ? (q?.modelAnswer || q?.answer || q?.explanation) : q?.explanation;
  const currentIsSearchResult = !searching || found.some(result => String(result._id) === String(id));
  const { intro: stmtIntro, closing: stmtClosing } = isStatement ? splitIntroClosing(q.question) : { intro: '', closing: '' };

  const questionBlock = () => {
    if (isMatch || hasLists) return (
      <>
        {q.matchIntro && <p className="pp-intro">{q.matchIntro}</p>}
        <MatchLists matchLists={q.matchLists} />
        <FormattedQuestion text={q.question} />
      </>
    );
    if (isAR) return (
      <>
        {q.assertion && (
          <div className="pp-ar">
            <p><b>Assertion (A):</b> {q.assertion}</p>
            <p><b>Reason (R):</b> {q.reason}</p>
          </div>
        )}
        <FormattedQuestion text={q.question} />
      </>
    );
    if (isStatement) return (
      <>
        <FormattedQuestion text={stmtIntro} />
        <ol className="pp-stmts">{q.statements.map((s, i) => <li key={i}>{s}</li>)}</ol>
        {stmtClosing && <FormattedQuestion text={stmtClosing} />}
      </>
    );
    return <FormattedQuestion text={q.question} />;
  };

  const optionsBlock = () => {
    if (isMains || !q.options?.length) return null;
    const done = picked !== null;
    const long = q.options.some(o => String(o).length > 48);
    return (
      <div className={`pp-opts ${long ? 'list' : ''}`}>
        {q.options.map((opt, i) => {
          const state = done ? (i === q.correctAnswer ? 'ok' : i === picked ? 'bad' : 'dim') : 'open';
          return (
            <button key={i} type="button" className={`pp-opt ${state}`} aria-disabled={done}
              onClick={() => { if (!done) setPicked(i); }}>
              <b>{String.fromCharCode(65 + i)}</b>
              <span>{opt}</span>
              {state === 'ok' && <Check size={18} />}
              {state === 'bad' && <X size={18} />}
            </button>
          );
        })}
      </div>
    );
  };

  /* SEO */
  const canonicalPath = `/pyq-question/${encodeURIComponent(q?.slug || id)}`;
  const seoTitle = q ? `${q.question?.slice(0, 75)} | ${q.exam} ${q.year || ''} PYQ | CivilFox` : 'Previous-year question | CivilFox';
  const seoDesc = q
    ? `${q.exam || 'Competitive exam'}${q.year ? ` ${q.year}` : ''} ${q.subject ? `· ${q.subject}` : ''}: ${String(q.question || '').replace(/\s+/g, ' ').slice(0, 150)}`
    : 'Open a previous-year question with answer and explanation.';

  return (
    <div className="pp" style={{ '--temple': `url(${templeImg})` }}>
      {q && (
        <PageMeta
          title={seoTitle}
          description={seoDesc}
          path={canonicalPath}
          type="article"
          structuredData={{
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seoTitle,
            description: seoDesc,
            url: `https://civilfox.app${canonicalPath}`,
            about: [q.exam, q.subject, q.topic].filter(Boolean),
          }}
        />
      )}

      {/* Panel: stays in place, only the question list scrolls */}
      <aside className={`pp-panel ${navOpen ? 'open' : ''}`}>
        <div className="pp-panel-top">
          <Link to="/" className="pp-logo" aria-label="CivilFox home"><img src={logo} alt="" /></Link>
          <button type="button" className="pp-x" onClick={() => setNavOpen(false)} aria-label="Close"><X size={18} /></button>
        </div>

        <nav className="pp-list" aria-label="Questions">
          {groups.map(([subject, idxs]) => (
            <section key={subject} className={`pp-sub ${idxs.some(i => items[i]._id === id) ? 'on' : ''}`}>
              <h2><i />{subject}</h2>
              <div className="pp-tiles">
                {idxs.map((i, n) => (
                  <button key={items[i]._id} type="button" className={`pp-tile ${items[i]._id === id ? 'cur' : ''}`}
                    aria-label={`${subject} ${n + 1}`} onClick={() => goTo(items[i]._id)}>{n + 1}</button>
                ))}
              </div>
            </section>
          ))}
        </nav>

        <div className="pp-foot">
          <Link to="/" className="pp-back"><ArrowLeft size={18} />Go back</Link>
          {!isLoggedIn && <Link to="/register" className="pp-signup">Sign up</Link>}
        </div>
      </aside>

      <main className="pp-main" ref={mainRef}>
        <div className="pp-bar">
          <button type="button" className="pp-grid-btn" onClick={() => setNavOpen(true)} aria-label="All questions">
            <LayoutGrid size={18} />
          </button>
          <label className="pp-search">
            <Search size={22} />
            <input
              type="search"
              enterKeyHint="search"
              placeholder="Search"
              aria-label="Search previous year questions"
              autoComplete="off"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </label>
        </div>

        <div className="pp-slice" role="presentation" />

        {searchPending ? (
          <div className="pp-state"><span className="pp-spin" role="status" aria-label="Searching" /></div>
        ) : searchError ? (
          <p className="pp-state">Search failed. Please try again.</p>
        ) : q && currentIsSearchResult ? (
          <article>
            <section className="pp-card">
            <div className="pp-meta">
              <div className="pp-tags">
                {q.exam && <span>{q.exam}</span>}
                {q.year && <span>{q.year}</span>}
                {q.subject && <span>{q.subject}</span>}
              </div>
              <button type="button" className="pp-share" onClick={share} aria-label="Share">
                {copied ? <Check size={16} /> : <Share2 size={16} />}
              </button>
            </div>

            <div className="pp-q">{questionBlock()}</div>
            {optionsBlock()}
            </section>
            {side && (isMains || picked !== null) && <div className="pp-a"><FormattedText text={side} /></div>}

            <div className="pp-comments"><CommentSection cardId={q._id} isLoggedIn={isLoggedIn} /></div>
          </article>
        ) : searching ? (
          <div className="pp-state">
            {found.length > 0 ? <span className="pp-spin" role="status" aria-label="Loading question" /> : 'No results.'}
          </div>
        ) : notFound ? (
          <p className="pp-state">Question not found.</p>
        ) : (
          <div className="pp-state"><span className="pp-spin" role="status" aria-label="Loading" /></div>
        )}
      </main>

      <div className={`pp-backdrop ${navOpen ? 'show' : ''}`} onClick={() => setNavOpen(false)} />
    </div>
  );
}